// src/pages/EmailCampaignManager.js
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import Papa from 'papaparse';
import * as XLSX from 'xlsx';

const MAX_FILE_SIZE = 20 * 1024 * 1024; // 20 MB
const MAX_ROWS      = 2000;             // approx. 2000 rows

const CREATE_CAMPAIGN_API_URL = process.env.REACT_APP_CREATE_CAMPAIGN_URL;
const PRESIGN_API_URL         = process.env.REACT_APP_PRESIGN_URL;
const GET_CAMPAIGNS_API_URL   = process.env.REACT_APP_ALL_CAMPAIGNS_URL;
const CANCEL_CAMPAIGN_API_URL = process.env.REACT_APP_CANCEL_CAMPAIGN_URL;
const FAVORITES_API_URL       = process.env.REACT_APP_FAVORITES_URL;
const SETTINGS_API_URL        = process.env.REACT_APP_SETTINGS_URL;

// Format timestamp into human-readable
const formatDate = dateStr => {
  const d = new Date(dateStr);
  return d.toLocaleString(undefined, {
    year: 'numeric', month: 'short', day: 'numeric',
    hour: '2-digit', minute: '2-digit'
  });
};

// Deep compare for React state
const deepEqual = (a, b) => JSON.stringify(a) === JSON.stringify(b);

function ConfirmationModal({ isOpen, message, onConfirm, onCancel }) {
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 flex items-center justify-center z-50">
      <div className="absolute inset-0 bg-black opacity-50" />
      <div className="bg-white p-6 rounded shadow-lg z-10 max-w-md mx-auto">
        <h2 className="text-xl font-bold mb-4">Confirm Cancellation</h2>
        <p className="mb-4">{message}</p>
        <div className="flex justify-end space-x-4">
          <button
            className="bg-gray-500 hover:bg-gray-600 text-white font-bold py-2 px-4 rounded"
            onClick={onCancel}
          >
            No
          </button>
          <button
            className="bg-red-500 hover:bg-red-600 text-white font-bold py-2 px-4 rounded"
            onClick={onConfirm}
          >
            Yes, Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

export default function EmailCampaignManager() {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  // Form state
  const [campaignName, setCampaignName]     = useState('');
  const [segmentFile, setSegmentFile]       = useState(null);
  const [genericDraft, setGenericDraft]     = useState('');
  const [genericDrafts, setGenericDrafts]   = useState([]);
  const [date, setDate]                     = useState('');
  const [lintErrors, setLintErrors]         = useState([]);
  const [rowError, setRowError]             = useState('');
  const [errorMessage, setErrorMessage]     = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [isSubmitting, setIsSubmitting]     = useState(false);

  // Email settings
  const [emailSetting, setEmailSetting]       = useState({ enabled: false, value: '' });
  const [settingsLoading, setSettingsLoading] = useState(true);
  const [settingsError, setSettingsError]     = useState(null);

  // Campaign list, pagination, filtering
  const [campaigns, setCampaigns]             = useState([]);
  const [editingCampaignId, setEditingCampaignId] = useState(null);
  const [currentPage, setCurrentPage]         = useState(1);
  const [pageSize, setPageSize]               = useState(10);
  const [filterStatus, setFilterStatus]       = useState('ALL');
  const [searchQuery, setSearchQuery]         = useState('');

  // Cancellation modal
  const [cancelLoadingId, setCancelLoadingId] = useState(null);
  const [confirmationModal, setConfirmationModal] = useState({
    isOpen: false, campaignId: null, campaignName: ''
  });

  // Auth
  const authDataString = localStorage.getItem('authData');
  const parsedAuthData = authDataString ? JSON.parse(authDataString) : null;
  const userId = parsedAuthData?.user?.sub;

  // Friendly messages derived from lintErrors
  const friendlyMessages = useMemo(() => {
    if (!lintErrors.length) return [];
    // Group Non-ASCII by field
    const nonAscii = {};
    // Track invalid-email rows
    const emailRows = [];
    lintErrors.forEach(({ row, message }) => {
      const m = message.match(/Non-ASCII in "(.+)"/);
      if (m) {
        const field = m[1];
        nonAscii[field] = nonAscii[field] || new Set();
        nonAscii[field].add(row);
      } else if (message === 'Invalid Email format') {
        emailRows.push(row);
      }
    });
    const msgs = [];
    // Build Non-ASCII messages
    for (const [field, rows] of Object.entries(nonAscii)) {
      const arr = Array.from(rows).sort((a,b) => a-b);
      if (arr.length === 1) {
        msgs.push(`Row ${arr[0]} contains non-standard characters in the ${field} field. Please remove them.`);
      } else {
        const last = arr.pop();
        msgs.push(
          `Rows ${arr.join(', ')} and ${last} contain non-standard characters in the ${field} field. Please remove them.`
        );
      }
    }
    // Build Email format message
    if (emailRows.length) {
      emailRows.sort((a,b) => a-b);
      if (emailRows.length === 1) {
        msgs.push(`Row ${emailRows[0]} has an invalid email address. Please correct it.`);
      } else {
        const last = emailRows.pop();
        msgs.push(
          `Rows ${emailRows.join(', ')} and ${last} have invalid email addresses. Please correct them.`
        );
      }
    }
    return msgs;
  }, [lintErrors]);

  // Fetch email setting
  useEffect(() => {
    if (!userId) return;
    (async () => {
      setSettingsLoading(true);
      setSettingsError(null);
      try {
        const res = await fetch(`${SETTINGS_API_URL}?userId=${userId}`);
        if (!res.ok) throw new Error(`Status ${res.status}`);
        const { body } = await res.json();
        const add1 = JSON.parse(body || '{}').settings?.additional1 || {};
        setEmailSetting({ enabled: !!add1.enabled, value: (add1.value || '').trim() });
      } catch (err) {
        console.error(err);
        setSettingsError("Couldn't load your email setting.");
      } finally {
        setSettingsLoading(false);
      }
    })();
  }, [userId]);

  // Fetch favorite drafts
  useEffect(() => {
    if (!userId) return;
    (async () => {
      try {
        const res = await fetch(FAVORITES_API_URL, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ userId })
        });
        if (!res.ok) throw new Error('Failed to fetch favorites.');
        const { body } = await res.json();
        const list = JSON.parse(body || '[]');
        setGenericDrafts(list.map(i => ({ id: i.id, name: i.nickName })));
      } catch (err) {
        console.error(err);
      }
    })();
  }, [userId]);

  // Helper to fetch campaigns
  const fetchCampaignsAndReturn = async () => {
    try {
      const res = await fetch(GET_CAMPAIGNS_API_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ body: JSON.stringify({ userId }) })
      });
      if (!res.ok) throw new Error(`Status ${res.status}`);
      const result = await res.json();
      let arr = result.body ? JSON.parse(result.body) : result;
      return Array.isArray(arr) ? arr : [];
    } catch (err) {
      console.error(err);
      setErrorMessage('Error fetching campaigns.');
      return [];
    }
  };

  // Update campaigns list
  const fetchCampaignsAndUpdate = async () => {
    const data = await fetchCampaignsAndReturn();
    setCampaigns(prev => deepEqual(prev, data) ? prev : data);
  };

  useEffect(() => { if (userId) fetchCampaignsAndUpdate(); }, [userId]);
  useEffect(() => {
    if (!userId) return;
    const id = setInterval(fetchCampaignsAndUpdate, 60000);
    return () => clearInterval(id);
  }, [userId]);

  // Handle file input: size, row-limit, lint
  const handleFileChange = async e => {
    setErrorMessage('');
    setRowError('');
    setLintErrors([]);
    const file = e.target.files[0];
    if (!file) {
      setSegmentFile(null);
      return;
    }
    // Byte-size check
    if (file.size > MAX_FILE_SIZE) {
      setErrorMessage(`File too large (max ${MAX_FILE_SIZE/1024/1024} MB).`);
      fileInputRef.current.value = '';
      return setSegmentFile(null);
    }
    // Extension check
    const ext = file.name.split('.').pop().toLowerCase();
    if (!['csv','xls','xlsx'].includes(ext)) {
      setErrorMessage('Must be .csv, .xls, or .xlsx');
      fileInputRef.current.value = '';
      return setSegmentFile(null);
    }
    // Row-count preview
    let rowCount = 0;
    try {
      if (ext === 'csv') {
        await new Promise((res, rej) => {
          Papa.parse(file, {
            preview: MAX_ROWS + 1,
            complete: results => {
              rowCount = results.data.length;
              res();
            },
            error: err => rej(err)
          });
        });
      } else {
        const data = await file.arrayBuffer();
        const wb   = XLSX.read(data, { type: 'array' });
        const ws   = wb.Sheets[wb.SheetNames[0]];
        const range = XLSX.utils.decode_range(ws['!ref'] || 'A1:A0');
        rowCount = range.e.r + 1;
      }
      if (rowCount > MAX_ROWS) {
        setRowError(`Your file has ${rowCount} rows; max allowed is ${MAX_ROWS}.`);
        fileInputRef.current.value = '';
        return setSegmentFile(null);
      }
    } catch (err) {
      console.error('Row count check failed', err);
      setErrorMessage('Could not read file to count rows.');
      fileInputRef.current.value = '';
      return setSegmentFile(null);
    }
    // Lint pass
    const errors = [];
    if (ext === 'csv') {
      let rowIndex = 1;
      await new Promise((res, rej) => {
        Papa.parse(file, {
          header: true,
          skipEmptyLines: true,
          step: ({ data, errors: pe }) => {
            rowIndex++;
            pe.forEach(e => errors.push({ row: rowIndex, message: e.message }));
            if (data.Email && !/\S+@\S+\.\S+/.test(data.Email)) {
              errors.push({ row: rowIndex, message: 'Invalid Email format' });
            }
            Object.entries(data).forEach(([k, v]) => {
              if (/[^\x20-\x7E]/.test(v || '')) {
                errors.push({ row: rowIndex, message: `Non-ASCII in "${k}"` });
              }
            });
          },
          complete: () => res(),
          error: err => rej(err)
        });
      });
    } else {
      const buf  = await file.arrayBuffer();
      const wb   = XLSX.read(buf, { type: 'array' });
      const ws   = wb.Sheets[wb.SheetNames[0]];
      const rows = XLSX.utils.sheet_to_json(ws, { header: 1 });
      const headers = rows[0] || [];
      rows.slice(1).forEach((row, i) => {
        const r = i + 2;
        const ei = headers.findIndex(h => h.toLowerCase() === 'email');
        const ev = ei >= 0 ? row[ei] : '';
        if (ev && !/\S+@\S+\.\S+/.test(ev)) {
          errors.push({ row: r, message: 'Invalid Email format' });
        }
        row.forEach((c, ci) => {
          if (/[^\x20-\x7E]/.test(c || '')) {
            const col = headers[ci] || `#${ci+1}`;
            errors.push({ row: r, message: `Non-ASCII in "${col}"` });
          }
        });
      });
    }
    if (errors.length) {
      setLintErrors(errors);
      fileInputRef.current.value = '';
      return setSegmentFile(null);
    }
    // All good
    setSegmentFile(file);
  };

  // Form validation
  const validateForm = () => {
    setErrorMessage('');
    if (!campaignName.trim()) {
      setErrorMessage('Campaign Name is required.'); return false;
    }
    if (!segmentFile) {
      setErrorMessage('You must upload a file.'); return false;
    }
    if (lintErrors.length) {
      setErrorMessage('Please fix the highlighted errors before submitting.'); return false;
    }
    if (!genericDraft) {
      setErrorMessage('Please select a favorite draft.'); return false;
    }
    if (!date) {
      setErrorMessage('Please choose a campaign date and time.'); return false;
    }
    if (new Date(date) < new Date()) {
      setErrorMessage('Campaign date/time must be in the future.'); return false;
    }
    if (!emailSetting.enabled || !emailSetting.value) {
      setErrorMessage('Enable & enter your Email in Settings first.'); return false;
    }
    return true;
  };

  // Upload to S3
  const uploadFileToS3 = async campaignId => {
    const ufid     = crypto?.randomUUID?.() || Date.now().toString();
    const filename = segmentFile.name;
    const s3Key    = `${userId}/${campaignId}/${ufid}/${filename}`;
    const presignRes = await fetch(PRESIGN_API_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ key: s3Key, contentType: segmentFile.type })
    });
    if (!presignRes.ok) throw new Error('Could not obtain presigned URL.');
    const { body } = await presignRes.json();
    const { url }  = JSON.parse(body);
    const uploadRes = await fetch(url, {
      method: 'PUT',
      headers: { 'Content-Type': segmentFile.type },
      body: segmentFile
    });
    if (!uploadRes.ok) {
      const txt = await uploadRes.text();
      throw new Error('S3 upload failed: ' + txt);
    }
    return { s3Key, ufid };
  };

  // Create campaign backend
  const createCampaignInBackend = async (campaignId, dateISO, filename, draftid, ufid) => {
    const payload = {
      campaignId: campaignId.toString(),
      userId,
      campaignName,
      filename,
      campaignDate: dateISO,
      draftid,
      ufid
    };
    const res = await fetch(CREATE_CAMPAIGN_API_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    });
    if (!res.ok) {
      const txt = await res.text();
      throw new Error('Backend create failed: ' + txt);
    }
  };

  // Handle form submit
  const handleFormSubmit = async e => {
    e.preventDefault();
    if (settingsLoading) return;
    if (!validateForm()) return;
    setIsSubmitting(true);
    try {
      const campaignId = editingCampaignId || Date.now();
      const { s3Key, ufid } = await uploadFileToS3(campaignId);
      const isoDate = new Date(date).toISOString();
      await createCampaignInBackend(campaignId, isoDate, s3Key, genericDraft, ufid);
      resetForm();
      await fetchCampaignsAndUpdate();
      setSuccessMessage('Campaign scheduled successfully!');
    } catch (err) {
      console.error(err);
      setErrorMessage('Error creating campaign. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Reset form
  const resetForm = () => {
    setCampaignName('');
    setSegmentFile(null);
    setGenericDraft('');
    setDate('');
    setEditingCampaignId(null);
    setErrorMessage('');
    setRowError('');
    setLintErrors([]);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  // Cancel campaign
  const cancelCampaign = async id => {
    const res = await fetch(CANCEL_CAMPAIGN_API_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ campaignId: id, user: userId })
    });
    if (!res.ok) throw new Error(`Cancel failed: ${res.status}`);
  };
  const waitForCancel = async id => {
    for (let i = 0; i < 10; i++) {
      const list = await fetchCampaignsAndReturn();
      if (list.find(c => c.campaignId === id)?.status.toLowerCase() === 'cancelled') {
        return true;
      }
      await new Promise(r => setTimeout(r, 1000));
    }
    return false;
  };
  const handleConfirmCancellation = async () => {
    const { campaignId, campaignName } = confirmationModal;
    setConfirmationModal({ isOpen: false, campaignId: null, campaignName: '' });
    setCancelLoadingId(campaignId);
    try {
      await cancelCampaign(campaignId);
      const ok = await waitForCancel(campaignId);
      if (ok) setSuccessMessage(`${campaignName} cancelled successfully.`);
      else setErrorMessage('Cancellation sent but not confirmed; refresh to check.');
      await fetchCampaignsAndUpdate();
    } catch {
      setErrorMessage('Error cancelling campaign. Please try again.');
    } finally {
      setCancelLoadingId(null);
    }
  };
  const openConfirmationModal = (id, name) =>
    setConfirmationModal({ isOpen: true, campaignId: id, campaignName: name });
  const closeConfirmationModal = () =>
    setConfirmationModal({ isOpen: false, campaignId: null, campaignName: '' });

  // Navigate to results
  const handleResults = c =>
    navigate('/emailResults', { state: { campaignId: c.campaignId, campaignName: c.campaignName } });

  // Filter, sort, paginate
  const filtered = campaigns.filter(c => {
    const byStatus = filterStatus === 'ALL' || c.status.toLowerCase() === filterStatus.toLowerCase();
    const bySearch = !searchQuery ||
      (c.campaignName?.toLowerCase() || '').includes(searchQuery.toLowerCase()) ||
      (c.filename?.toLowerCase() || '').includes(searchQuery.toLowerCase());
    return byStatus && bySearch;
  });
  const sortedList = [...filtered].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
  const totalPages = Math.ceil(sortedList.length / pageSize);
  const paged = sortedList.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  return (
    <Layout>
      <div className="container mx-auto mt-10 max-w-4xl">
        {settingsError && <div className="mb-4 text-red-600 font-bold">{settingsError}</div>}
        {successMessage && <div className="mb-4 text-green-600 font-bold">{successMessage}</div>}
        {errorMessage && <div className="mb-4 text-red-600 font-bold">{errorMessage}</div>}

        <ConfirmationModal
          isOpen={confirmationModal.isOpen}
          message={`Cancel "${confirmationModal.campaignName}"?`}
          onConfirm={handleConfirmCancellation}
          onCancel={closeConfirmationModal}
        />

        <div className="bg-white shadow-md p-10 rounded-md">
          <h1 className="text-xl font-semibold mb-6">
            {editingCampaignId ? 'Edit Campaign' : 'Create Personalized Email Campaigns'}
          </h1>
          <form onSubmit={handleFormSubmit}>

            {/* Campaign Name */}
            <div className="mb-4">
              <label htmlFor="campaignName" className="block text-gray-700 font-bold mb-2">
                Campaign Name
              </label>
              <input
                id="campaignName"
                type="text"
                className="border p-2 w-full rounded"
                value={campaignName}
                onChange={e => setCampaignName(e.target.value)}
                required
              />
            </div>

            {/* Segment File Upload */}
            <div className="mb-4">
              <label htmlFor="segmentList" className="block text-gray-700 font-bold mb-2">
                Upload Segment List (xls, xlsx, or csv)
              </label>
              <input
                id="segmentList"
                type="file"
                accept=".csv,.xls,.xlsx"
                ref={fileInputRef}
                onChange={handleFileChange}
                className="border p-2 w-full rounded"
              />
              {rowError && <div className="mt-2 text-red-600 font-semibold">{rowError}</div>}
              {friendlyMessages.map((msg, i) => (
                <div key={i} className="mt-2 text-red-600">{msg}</div>
              ))}
              <div className="mt-2">
                <a href="/assets/sample_csv_contacts.csv" download className="text-blue-500 hover:underline">
                  Download Sample File
                </a>
              </div>
            </div>

            {/* Favorite Draft */}
            <div className="mb-4">
              <label htmlFor="genericDraft" className="block text-gray-700 font-bold mb-2">
                Select Favorite Draft
              </label>
              <select
                id="genericDraft"
                className="border p-2 w-full rounded"
                value={genericDraft}
                onChange={e => setGenericDraft(e.target.value)}
              >
                <option value="">-- Select a Draft --</option>
                {genericDrafts.map(d => (
                  <option key={d.id} value={d.id}>{d.name}</option>
                ))}
              </select>
            </div>

            {/* Campaign Date & Time */}
            <div className="mb-4">
              <label htmlFor="date" className="block text-gray-700 font-bold mb-2">
                Campaign Date & Time
              </label>
              <input
                id="date"
                type="datetime-local"
                className="border p-2 w-full rounded"
                value={date}
                onChange={e => setDate(e.target.value)}
              />
            </div>

            {/* Actions */}
            <div className="mt-6 flex space-x-4">
              <button
                type="submit"
                disabled={isSubmitting || settingsLoading}
                className="bg-blue-500 hover:bg-blue-600 text-white font-bold py-2 px-4 rounded flex items-center"
              >
                {isSubmitting ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-white mr-2" />
                    Sending…
                  </>
                ) : editingCampaignId ? 'Save Campaign' : 'Send Personalized Emails'}
              </button>
              {editingCampaignId && (
                <button
                  type="button"
                  className="bg-gray-500 hover:bg-gray-600 text-white font-bold py-2 px-4 rounded"
                  onClick={resetForm}
                >
                  Cancel
                </button>
              )}
            </div>
          </form>
        </div>

        {/* Campaigns Table */}
        <div className="py-12 bg-white sm:py-16 lg:py-20">
          <div className="px-4 mx-auto max-w-7xl sm:px-6 lg:px-8">
            <div className="flex items-center justify-between mb-4">
              <p className="text-base font-bold text-gray-900">Campaigns</p>
              <div className="flex space-x-4">
                <select
                  value={filterStatus}
                  onChange={e => { setFilterStatus(e.target.value); setCurrentPage(1); }}
                  className="px-4 py-2 rounded bg-gray-200"
                >
                  {['ALL','Pending','Running','Completed','Cancelled'].map(s => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
                <input
                  type="text"
                  value={searchQuery}
                  onChange={e => { setSearchQuery(e.target.value); setCurrentPage(1); }}
                  placeholder="Search campaigns…"
                  className="px-4 py-2 rounded border"
                />
              </div>
            </div>

            <div className="mt-6 ring-1 ring-gray-300 rounded-2xl overflow-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="hidden lg:table-header-group">
                  <tr>
                    <th className="px-6 py-4 text-left text-sm font-medium text-gray-400">Campaign Name</th>
                    <th className="px-6 py-4 text-left text-sm font-medium text-gray-400">Status</th>
                    <th className="px-6 py-4 text-left text-sm font-medium text-gray-400">Created At</th>
                    <th className="px-6 py-4 text-left text-sm font-medium text-gray-400">Action</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {paged.map(c => (
                    <tr key={c.campaignId}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-bold">
                        {c.campaignName || c.filename}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">{c.status}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        {formatDate(c.createdAt)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        {c.stats === 'Y' ? (
                          <button
                            className="px-4 py-2 bg-green-500 text-white rounded"
                            onClick={() => handleResults(c)}
                          >
                            Results
                          </button>
                        ) : c.status === 'Pending' ? (
                          cancelLoadingId === c.campaignId ? (
                            <button disabled className="px-4 py-2 bg-red-500 text-white rounded opacity-50">
                              Cancelling…
                            </button>
                          ) : (
                            <button
                              className="px-4 py-2 bg-red-500 text-white rounded"
                              onClick={() => openConfirmationModal(c.campaignId, c.campaignName || c.filename)}
                            >
                              Cancel
                            </button>
                          )
                        ) : null}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <nav className="mt-6 flex justify-center space-x-2">
                {Array.from({ length: totalPages }, (_, i) => i + 1).map(p => (
                  <button
                    key={p}
                    onClick={() => setCurrentPage(p)}
                    className={`w-8 h-8 rounded-full text-sm ${
                      currentPage === p ? 'bg-gray-900 text-white' : 'text-gray-400 hover:bg-gray-200'
                    }`}
                  >
                    {p}
                  </button>
                ))}
              </nav>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
}
